import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, CheckCircle2, XCircle, Sparkles, Award, Target, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function MSAPage() {
  const { getAuthHeader } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [examDates, setExamDates] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('mathe');
  const [generating, setGenerating] = useState(false);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchExamDates().then((existingDates) => {
      if (!existingDates || existingDates.length === 0) {
        initExamDates();
      }
    });
    fetchExercises();
  }, []);

  const initExamDates = async () => {
    try {
      const headers = getAuthHeader();
      await axios.post(`${BACKEND_URL}/api/msa/exam-dates/init`, {}, { headers });
      fetchExamDates();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchExamDates = async () => {
    try {
      const headers = getAuthHeader();
      const response = await axios.get(`${BACKEND_URL}/api/msa/exam-dates`, { headers });
      setExamDates(response.data);
      return response.data;
    } catch (error) {
      console.error('Error:', error);
      return [];
    }
  };

  const fetchExercises = async () => {
    try {
      const headers = getAuthHeader();
      const response = await axios.get(`${BACKEND_URL}/api/msa/exercises`, { headers });
      setExercises(response.data || []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Get exercises for selected subject only
  const subjectExercises = exercises.filter(ex => ex.subject === selectedSubject);
  const answeredExercises = subjectExercises.filter(ex => ex.user_answer !== null);
  const correctExercises = subjectExercises.filter(ex => ex.is_correct === true);
  const progressPercent = subjectExercises.length > 0 ? Math.round((correctExercises.length / subjectExercises.length) * 100) : 0;

  // Get unanswered exercises for practice
  const unansweredExercises = subjectExercises.filter(ex => ex.user_answer === null || ex.user_answer === undefined);

  const handleGenerateExercises = async () => {
    setGenerating(true);
    try {
      const headers = getAuthHeader();
      await axios.post(
        `${BACKEND_URL}/api/msa/generate-exercises-direct?subject=${selectedSubject}&count=5`,
        {},
        { headers }
      );
      toast.success('Neue Übungen erstellt! 🎉');
      fetchExercises();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Erstellen der Übungen');
    } finally {
      setGenerating(false);
    }
  };

  const startExercise = (exercise) => {
    setCurrentExercise(exercise);
    setUserAnswer('');
    setResult(null);
  };

  const startPractice = () => {
    if (unansweredExercises.length > 0) {
      startExercise(unansweredExercises[0]);
    } else if (subjectExercises.length > 0) {
      // All answered, start from beginning
      startExercise(subjectExercises[0]);
    } else {
      toast.info('Keine Übungen vorhanden. Klicke auf "Neue Übungen erstellen"');
    }
  };

  const handleCheckAnswer = async () => {
    if (!userAnswer.trim()) {
      toast.error('Bitte gib eine Antwort ein');
      return;
    }

    setChecking(true);
    try {
      const headers = getAuthHeader();
      const response = await axios.post(
        `${BACKEND_URL}/api/msa/exercises/${currentExercise.id}/check`,
        { user_answer: userAnswer },
        { headers }
      );

      setResult(response.data);
      
      if (response.data.is_correct) {
        toast.success('Richtig! +15 Punkte 🎉');
      }
      
      fetchExercises();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Prüfen');
    } finally {
      setChecking(false);
    }
  };

  const nextExercise = () => {
    const currentIndex = subjectExercises.findIndex(ex => ex.id === currentExercise?.id);
    if (currentIndex < subjectExercises.length - 1) {
      startExercise(subjectExercises[currentIndex + 1]);
    } else {
      setCurrentExercise(null);
      toast.success('Alle Übungen für dieses Fach bearbeitet! 🏆');
    }
  };

  const exitPractice = () => {
    setCurrentExercise(null);
    setResult(null);
    setUserAnswer('');
    fetchExercises();
  };

  // Subject info
  const subjects = {
    mathe: { name: 'Mathematik', emoji: '📐', color: 'from-blue-500 to-indigo-600' },
    deutsch: { name: 'Deutsch', emoji: '📖', color: 'from-green-500 to-emerald-600' },
    englisch: { name: 'Englisch', emoji: '🇬🇧', color: 'from-red-500 to-orange-600' }
  };

  // Unique exam dates
  const uniqueExamDates = examDates.filter((date, index, self) =>
    index === self.findIndex((d) => d.subject === date.subject)
  );

  // Practice Mode
  if (currentExercise) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className={`bg-gradient-to-r ${subjects[selectedSubject].color} rounded-3xl p-6 text-white`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-white/80 text-sm">MSA Übung</p>
              <h2 className="text-2xl font-heading font-bold">
                {subjects[selectedSubject].emoji} {subjects[selectedSubject].name}
              </h2>
            </div>
            <Button onClick={exitPractice} variant="outline" className="bg-white/20 border-white/30 text-white hover:bg-white/30">
              Beenden
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span>Fortschritt</span>
                <span>{correctExercises.length}/{subjectExercises.length} richtig</span>
              </div>
              <Progress value={progressPercent} className="h-3 bg-white/20" />
            </div>
            <div className="text-3xl font-bold">{progressPercent}%</div>
          </div>
        </div>

        {/* Question */}
        <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                currentExercise.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                currentExercise.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-green-100 text-green-700'
              }`}>
                {currentExercise.difficulty === 'hard' ? 'Schwer' : currentExercise.difficulty === 'medium' ? 'Mittel' : 'Einfach'}
              </span>
              <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-bold rounded-full">
                {currentExercise.topic}
              </span>
            </div>
            
            <h3 className="text-2xl font-heading font-bold text-slate-900 mb-4">Aufgabe</h3>
            <p className="text-lg text-slate-700 leading-relaxed whitespace-pre-wrap">
              {currentExercise.question}
            </p>
          </div>

          {!result ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Deine Antwort:</label>
                <Textarea
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  placeholder="Schreibe hier deine Antwort..."
                  className="min-h-[120px] rounded-2xl"
                  data-testid="answer-input"
                />
              </div>
              <Button
                onClick={handleCheckAnswer}
                disabled={checking || !userAnswer.trim()}
                className="w-full h-12 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-bold"
              >
                {checking ? 'Prüfe...' : 'Antwort prüfen'}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className={`p-6 rounded-2xl ${result.is_correct ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                <div className="flex items-center gap-3 mb-3">
                  {result.is_correct ? (
                    <>
                      <CheckCircle2 className="text-green-600" size={32} />
                      <div>
                        <p className="text-xl font-heading font-bold text-green-900">Richtig! 🎉</p>
                        <p className="text-sm text-green-700">+15 Punkte</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <XCircle className="text-red-600" size={32} />
                      <p className="text-xl font-heading font-bold text-red-900">Nicht ganz richtig</p>
                    </>
                  )}
                </div>
                <p className="text-slate-700">{result.feedback}</p>
              </div>

              <div className="p-6 bg-blue-50 rounded-2xl border border-blue-200">
                <p className="font-bold text-blue-900 mb-2">Korrekte Antwort:</p>
                <p className="text-blue-800 mb-4 whitespace-pre-wrap">{result.correct_answer}</p>
                <p className="font-bold text-blue-900 mb-2">Erklärung:</p>
                <p className="text-blue-700 text-sm whitespace-pre-wrap">{result.explanation}</p>
              </div>

              <Button
                onClick={nextExercise}
                className="w-full h-12 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-bold"
              >
                <ArrowRight size={20} className="mr-2" />
                Nächste Aufgabe
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main View
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-heading font-bold text-slate-900 mb-2">MSA-Prüfungsvorbereitung 2026</h1>
        <p className="text-lg text-slate-600">Mittlerer Schulabschluss: Mathe, Englisch, Deutsch</p>
      </div>

      {/* Countdown Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {uniqueExamDates.map(exam => (
          <div 
            key={exam.subject} 
            className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
              selectedSubject === exam.subject 
                ? 'border-primary-600 bg-primary-50 shadow-lg' 
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
            onClick={() => setSelectedSubject(exam.subject)}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">{subjects[exam.subject]?.emoji}</span>
              <span className="font-bold text-slate-900">{subjects[exam.subject]?.name}</span>
              {selectedSubject === exam.subject && (
                <span className="ml-auto px-2 py-0.5 bg-primary-600 text-white text-xs rounded-full">Ausgewählt</span>
              )}
            </div>
            <p className="text-3xl font-heading font-bold text-primary-600">{exam.days_until} Tage</p>
            <p className="text-sm text-slate-600">{new Date(exam.exam_date).toLocaleDateString('de-DE', { day: 'numeric', month: 'long' })}</p>
            
            {/* Progress for this subject */}
            <div className="mt-3">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Vorbereitung</span>
                <span>{exercises.filter(e => e.subject === exam.subject && e.is_correct).length}/{exercises.filter(e => e.subject === exam.subject).length}</span>
              </div>
              <Progress 
                value={exercises.filter(e => e.subject === exam.subject).length > 0 
                  ? (exercises.filter(e => e.subject === exam.subject && e.is_correct).length / exercises.filter(e => e.subject === exam.subject).length) * 100 
                  : 0} 
                className="h-2" 
              />
            </div>
          </div>
        ))}
      </div>

      {/* Selected Subject Section */}
      <div className={`bg-gradient-to-r ${subjects[selectedSubject].color} rounded-3xl p-6 text-white`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-heading font-bold">
              {subjects[selectedSubject].emoji} {subjects[selectedSubject].name} üben
            </h2>
            <p className="text-white/80">
              {subjectExercises.length} Übungen verfügbar • {correctExercises.length} richtig beantwortet
            </p>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold">{progressPercent}%</p>
            <p className="text-white/80 text-sm">Fortschritt</p>
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button
            onClick={startPractice}
            disabled={subjectExercises.length === 0}
            className="flex-1 h-14 rounded-2xl bg-white text-slate-900 hover:bg-white/90 font-bold text-lg"
          >
            <Target size={20} className="mr-2" />
            {unansweredExercises.length > 0 
              ? `Jetzt üben (${unansweredExercises.length} offen)` 
              : subjectExercises.length > 0 
                ? 'Alle nochmal üben' 
                : 'Keine Übungen'}
          </Button>
          <Button
            onClick={handleGenerateExercises}
            disabled={generating}
            className="h-14 px-6 rounded-2xl bg-white/20 hover:bg-white/30 text-white font-bold"
          >
            <Sparkles size={20} className="mr-2" />
            {generating ? 'Erstelle...' : 'Neue Übungen'}
          </Button>
        </div>
      </div>

      {/* Exercise List for Selected Subject */}
      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm">
        <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">
          Übungen: {subjects[selectedSubject].name}
        </h2>
        
        {subjectExercises.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-center">
            <div>
              <BookOpen className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-600 mb-4">Noch keine Übungen für {subjects[selectedSubject].name}</p>
              <Button onClick={handleGenerateExercises} disabled={generating} className="rounded-full">
                <Sparkles size={18} className="mr-2" />
                Übungen erstellen
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {subjectExercises.map((ex, index) => (
              <div 
                key={ex.id} 
                className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                  ex.is_correct === true
                    ? 'bg-green-50 border-green-200'
                    : ex.is_correct === false
                      ? 'bg-red-50 border-red-200'
                      : 'bg-slate-50 border-slate-200 hover:border-primary-300'
                }`}
                onClick={() => startExercise(ex)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="px-2 py-0.5 bg-slate-200 text-slate-700 text-xs font-bold rounded-full">
                        #{index + 1}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        ex.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                        ex.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {ex.difficulty === 'hard' ? 'Schwer' : ex.difficulty === 'medium' ? 'Mittel' : 'Einfach'}
                      </span>
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-bold rounded-full">
                        {ex.topic}
                      </span>
                      {ex.is_correct === true && (
                        <span className="px-2 py-0.5 bg-green-600 text-white text-xs font-bold rounded-full">
                          ✓ Richtig
                        </span>
                      )}
                      {ex.is_correct === false && (
                        <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded-full">
                          ✗ Falsch
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 line-clamp-2">{ex.question}</p>
                  </div>
                  <Target className="text-slate-400 flex-shrink-0 ml-4" size={20} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MSAPage;
